'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavItem {
  title: string;
  href: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    label: 'Getting Started',
    items: [
      { title: 'Overview', href: '/docs' },
      { title: 'Quickstart', href: '/docs/quickstart' },
    ],
  },
  {
    label: 'Core Concepts',
    items: [
      { title: 'Modules', href: '/docs/modules' },
      { title: 'Projects & Groups', href: '/docs/projects' },
      { title: 'Environments', href: '/docs/environments' },
      { title: 'Profiles', href: '/docs/profiles' },
      { title: 'Templates', href: '/docs/templates' },
      { title: 'Packages', href: '/docs/packages' },
      { title: 'Secrets', href: '/docs/secrets' },
      { title: 'Machine Tags & Vars', href: '/docs/machine' },
    ],
  },
  {
    label: 'Sync',
    items: [
      { title: 'Push & Pull', href: '/docs/push-pull' },
      { title: 'Selective Sync', href: '/docs/selective-sync' },
      { title: 'History & Rollback', href: '/docs/history' },
      { title: 'Watch Mode', href: '/docs/watch' },
      { title: 'Hooks', href: '/docs/hooks' },
    ],
  },
  {
    label: 'Advanced',
    items: [
      { title: 'Diff', href: '/docs/diff' },
      { title: 'Bootstrap', href: '/docs/bootstrap' },
    ],
  },
  {
    label: 'Reference',
    items: [
      { title: 'Security Model', href: '/docs/security-model' },
      { title: 'Plugin Development', href: '/docs/plugin-development' },
    ],
  },
];

export default function DocsSidebar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebar = (
    <nav className="space-y-6">
      {navigation.map((section) => (
        <div key={section.label}>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
            {section.label}
          </h4>
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const active = pathname === item.href;
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-3 py-1.5 rounded-md text-sm transition ${
                      active
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed bottom-4 right-4 z-50 bg-primary-600 text-white p-3 rounded-full shadow-lg"
        aria-label="Toggle docs navigation"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {mobileOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      <div className={`lg:hidden fixed inset-y-0 left-0 z-40 w-72 bg-white border-r border-gray-200 p-6 overflow-y-auto transform transition-transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900">Documentation</h3>
        </div>
        {sidebar}
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-24 overflow-y-auto max-h-[calc(100vh-8rem)] pb-8">
          {sidebar}
        </div>
      </aside>
    </>
  );
}
